import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './config';
import {
  createStorageMetadata,
  recordDocumentMetadata,
  type DocumentUploadContext,
} from './documentRegistry';
import { sanitizeFileName } from '@/utils/fileNameSanitizer';

export interface ContractTemplateField {
  id?: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'email' | 'tel' | 'textarea' | 'select' | 'checkbox' | 'vendor' | 'location' | 'locations' | 'asset' | 'assets' | 'status' | 'contact';
  required?: boolean;
  description?: string;
  options?: string[]; // For select type
  defaultValue?: string;
  order?: number; // Order in which fields appear
}

export interface AgreementDocument {
  id?: string;
  name: string;
  description?: string;
  fileUrl?: string; // URL to the document file (PDF, etc.)
  fileType?: string; // 'pdf', 'docx', etc.
  status: 'draft' | 'sent' | 'signed' | 'cancelled' | 'expired';
  signers: AgreementSigner[]; // Array of people who need to sign
  organizationId?: string;
  templateId?: string; // Associated compliance template
  owner_role?: string; // Owner role from compliance template for access control
  locationId?: string; // Optional: associated with a location
  locationName?: string;
  contractType?: 'vendor_contract' | 'employee_contract'; // Type of contract
  vendorId?: string; // Optional: associated vendor (for contract completions, not template)
  vendorName?: string; // Vendor name for display (denormalized)
  employeeId?: string; // Optional: associated employee (for contract completions, not template)
  employeeName?: string; // Employee name for display (denormalized)
  expiresAt?: any; // Expiration date for the signature request
  sentAt?: any; // Timestamp when the document was sent for signing
  completedAt?: any; // When all signatures were completed
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string; // User who created the document
  customFields?: ContractTemplateField[]; // Custom fields for this contract template
}

export interface AgreementSigner {
  email: string;
  name: string;
  role?: string; // 'signer', 'approver', 'viewer', etc.
  status: 'pending' | 'sent' | 'viewed' | 'signed' | 'declined';
  signedAt?: any;
  declineReason?: string;
  signatureUrl?: string; // URL to the signed document page
}

// Helper function to remove undefined values from an object (Firestore doesn't accept undefined)
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
};

export interface AgreementUploadOptions extends Partial<DocumentUploadContext> {}

// Upload file to Firebase Storage and get URL
export async function uploadAgreementDocument(
  file: File,
  options: AgreementUploadOptions = {}
): Promise<string> {
  // Validate file size before attempting upload
  const { validateFileSize } = await import('@/utils/fileSizeValidation');
  validateFileSize(file);

  if (!storage) {
    console.error('❌ Firebase Storage not initialized');
    throw new Error('Firebase Storage not initialized. Please check your Firebase configuration.');
  }

  // Refresh Firebase Auth token before upload to prevent permission errors
  try {
    const { auth } = await import('./config');
    const { getIdToken } = await import('firebase/auth');
    if (auth?.currentUser) {
      console.log('🔄 Refreshing Firebase Auth token before upload...');
      await getIdToken(auth.currentUser, true); // Force refresh
      console.log('✅ Token refreshed successfully');
    }
  } catch (tokenError: any) {
    console.warn('⚠️ Failed to refresh token before upload:', tokenError);
    // Continue with upload attempt - it will fail with a clear error if token is invalid
  }

  try {
    const docType = options.docType || 'agreement_document';
    const uploadedAt = new Date().toISOString();
    
    // Fetch owner_role from the compliance template if templateId is provided
    let ownerRole: string | undefined = options.additionalMetadata?.owner_role as string | undefined;
    if (!ownerRole && options.templateId) {
      try {
        console.log('🔍 Fetching owner_role for agreement document from template:', options.templateId);
        // Use dynamic import to avoid circular dependency
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./config');
        const templateRef = doc(db, 'compliance_templates', options.templateId);
        const templateSnap = await getDoc(templateRef);
        if (templateSnap.exists()) {
          const templateData = templateSnap.data();
          ownerRole = templateData.owner_role;
          console.log('✅ Found owner_role from template:', ownerRole);
        } else {
          console.warn('⚠️ Template not found:', options.templateId);
        }
      } catch (error) {
        console.error('❌ Error fetching compliance template for owner_role:', error);
        // Continue without owner_role if template fetch fails
      }
    } else if (ownerRole) {
      console.log('✅ Using owner_role from additionalMetadata:', ownerRole);
    } else {
      console.warn('⚠️ No owner_role available - templateId:', options.templateId);
    }
    
    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const sanitizedFileName = sanitizeFileName(file.name);
    const fileName = `agreements/${timestamp}_${sanitizedFileName}`;
    
    console.log('[AgreementService] Filename sanitization', {
      original: file.name,
      sanitized: sanitizedFileName,
      finalPath: fileName,
    });
    
    // Create a storage reference
    const storageRef = ref(storage, fileName);
    const storageContext: DocumentUploadContext = {
      docType,
      organizationId: options.organizationId,
      locationId: options.locationId,
      templateId: options.templateId,
      agreementId: options.agreementId,
      uploadedBy: options.uploadedBy,
      title: options.title || file.name,
      source: options.source || 'web',
      additionalMetadata: {
        ...options.additionalMetadata,
        ...(ownerRole && { owner_role: ownerRole }),
      },
    };
    
    // Log the storage context to verify owner_role is included
    console.log('📤 Uploading agreement document with storage context:', {
      templateId: storageContext.templateId,
      owner_role: ownerRole,
      additionalMetadata: storageContext.additionalMetadata,
    });
    
    // Create metadata for Storage
    const storageMetadata = createStorageMetadata(storageContext, {
      contentType: file.type,
      uploadedAt,
    });
    
    console.log('📦 Storage metadata to be uploaded:', {
      customMetadata: storageMetadata.customMetadata,
      contentType: storageMetadata.contentType,
    });
    
    // Upload the file
    await uploadBytes(
      storageRef,
      file,
      storageMetadata
    );
    
    console.log('✅ File uploaded to Storage with metadata');
    
    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);
    console.log('🔗 Download URL:', downloadURL);

    await recordDocumentMetadata({
      storagePath: storageRef.fullPath,
      bucket: (storageRef as any).bucket,
      downloadUrl: downloadURL,
      mimeType: file.type,
      sizeBytes: file.size,
      uploadedAt,
      context: storageContext,
    });
    
    console.log('✅ Document metadata recorded in Firestore');
    
    return downloadURL;
  } catch (error: any) {
    console.error('❌ Error uploading agreement document:', error);
    
    // Check if error might be due to file size (Firebase rules reject large files with permission error)
    const { MAX_FILE_SIZE, formatFileSize } = await import('@/utils/fileSizeValidation');
    if (file.size > MAX_FILE_SIZE) {
      const fileSizeFormatted = formatFileSize(file.size);
      const maxSizeFormatted = formatFileSize(MAX_FILE_SIZE);
      throw new Error(
        `File "${file.name}" is too large (${fileSizeFormatted}). Maximum file size is ${maxSizeFormatted}. Please compress the file or choose a smaller file.`
      );
    }

    // If it's an auth/permission error, try to refresh token and retry once
    if (error.code === 'storage/unauthorized' || error.code === 'storage/unauthenticated') {
      try {
        const { auth } = await import('./config');
        const { getIdToken, reload } = await import('firebase/auth');
        if (auth?.currentUser) {
          console.log('🔄 Permission error detected, attempting token refresh and retry...');
          await reload(auth.currentUser);
          await getIdToken(auth.currentUser, true); // Force refresh
          console.log('✅ Token refreshed, retrying upload...');
          
          // Retry the upload once after token refresh
          try {
            const retryDocType = options.docType || 'agreement_document';
            const retryUploadedAt = new Date().toISOString();
            const retryTimestamp = Date.now();
            const retrySanitizedFileName = sanitizeFileName(file.name);
            const retryFileName = `agreements/${retryTimestamp}_${retrySanitizedFileName}`;
            const retryStorageRef = ref(storage, retryFileName);
            
            // Fetch owner_role again if needed
            let retryOwnerRole: string | undefined = options.additionalMetadata?.owner_role as string | undefined;
            if (!retryOwnerRole && options.templateId) {
              try {
                const { doc, getDoc } = await import('firebase/firestore');
                const { db } = await import('./config');
                const templateRef = doc(db, 'compliance_templates', options.templateId);
                const templateSnap = await getDoc(templateRef);
                if (templateSnap.exists()) {
                  retryOwnerRole = templateSnap.data().owner_role;
                }
              } catch (e) {
                // Ignore errors fetching owner_role on retry
              }
            }
            
            const retryStorageContext: DocumentUploadContext = {
              docType: retryDocType,
              organizationId: options.organizationId,
              locationId: options.locationId,
              templateId: options.templateId,
              agreementId: options.agreementId,
              uploadedBy: options.uploadedBy,
              title: options.title || file.name,
              source: options.source || 'web',
              additionalMetadata: {
                ...options.additionalMetadata,
                ...(retryOwnerRole && { owner_role: retryOwnerRole }),
              },
            };
            
            const retryStorageMetadata = createStorageMetadata(retryStorageContext, {
              contentType: file.type,
              uploadedAt: retryUploadedAt,
            });
            
            await uploadBytes(retryStorageRef, file, retryStorageMetadata);
            const retryDownloadURL = await getDownloadURL(retryStorageRef);
            
            await recordDocumentMetadata({
              storagePath: retryStorageRef.fullPath,
              bucket: (retryStorageRef as any).bucket,
              downloadUrl: retryDownloadURL,
              mimeType: file.type,
              sizeBytes: file.size,
              uploadedAt: retryUploadedAt,
              context: retryStorageContext,
            });
            
            console.log('✅ Upload succeeded after token refresh');
            return retryDownloadURL;
          } catch (retryError: any) {
            console.error('❌ Retry after token refresh also failed:', retryError);
            // Fall through to show error message
          }
        }
      } catch (refreshError) {
        console.error('❌ Failed to refresh token:', refreshError);
        // Import and trigger auth error handler for automatic logout if needed
        try {
          const { handleFirebaseAuthError } = await import('@/utils/firebaseErrorHandler');
          await handleFirebaseAuthError(error);
        } catch (handlerError) {
          console.error('❌ Error handler failed:', handlerError);
        }
      }
      
      throw new Error('Permission denied. Your session may have expired. Please try refreshing the page and logging in again.');
    } else if (error.code === 'storage/quota-exceeded') {
      throw new Error('Storage quota exceeded. Please contact administrator.');
    } else if (error.code === 'storage/unknown') {
      throw new Error('Unknown storage error. Please try again.');
    } else {
      throw new Error(`File upload failed: ${error.message}`);
    }
  }
}

export const agreementService = {
  // Create a new agreement document
  createDocument: async (documentData: Omit<AgreementDocument, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      // If templateId is provided, fetch the compliance template to get owner_role
      let ownerRole: string | undefined = documentData.owner_role;
      if (!ownerRole && documentData.templateId) {
        try {
          const { complianceService } = await import('./complianceService');
          const templateResult = await complianceService.getTemplate(documentData.templateId);
          if (templateResult.success && templateResult.data) {
            ownerRole = templateResult.data.owner_role;
          }
        } catch (error) {
          console.warn('Error fetching compliance template for owner_role:', error);
          // Continue without owner_role if template fetch fails
        }
      }
      
      const documentsRef = collection(db, 'eSignDocuments'); // Keep collection name for backward compatibility
      // Clean undefined values before saving
      const cleanedData = removeUndefined({
        ...documentData,
        owner_role: ownerRole,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      const docRef = await addDoc(documentsRef, cleanedData);
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error creating agreement document:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single document by ID
  getDocument: async (documentId: string) => {
    try {
      const docRef = doc(db, 'eSignDocuments', documentId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } as AgreementDocument };
      } else {
        return { success: false, error: 'Document not found' };
      }
    } catch (error: any) {
      console.error('Error getting agreement document:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all documents for an organization
  getAllDocuments: async (organizationId?: string) => {
    try {
      const documentsRef = collection(db, 'eSignDocuments');
      let q;
      
      if (organizationId) {
        // Query with organizationId filter - orderBy requires an index, so we'll get all and sort in memory
        q = query(documentsRef, where('organizationId', '==', organizationId));
      } else {
        // If no organizationId, just order by createdAt
        q = query(documentsRef, orderBy('createdAt', 'desc'));
      }
      
      const querySnapshot = await getDocs(q);
      const documents: AgreementDocument[] = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() } as AgreementDocument);
      });
      
      // Sort by createdAt in memory if we filtered by organizationId
      if (organizationId && documents.length > 0) {
        documents.sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 
                       (a.createdAt instanceof Date ? a.createdAt.getTime() : 
                       (typeof a.createdAt === 'number' ? a.createdAt : 0));
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 
                       (b.createdAt instanceof Date ? b.createdAt.getTime() : 
                       (typeof b.createdAt === 'number' ? b.createdAt : 0));
          return bDate - aDate; // Descending order (newest first)
        });
      }
      
      return { success: true, data: documents };
    } catch (error: any) {
      console.error('Error getting agreement documents:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a document
  updateDocument: async (documentId: string, updates: Partial<AgreementDocument>) => {
    try {
      // If templateId is being updated, fetch the compliance template to get owner_role
      let ownerRole: string | undefined = updates.owner_role;
      if (!ownerRole && updates.templateId) {
        try {
          const { complianceService } = await import('./complianceService');
          const templateResult = await complianceService.getTemplate(updates.templateId);
          if (templateResult.success && templateResult.data) {
            ownerRole = templateResult.data.owner_role;
          }
        } catch (error) {
          console.warn('Error fetching compliance template for owner_role:', error);
          // Continue without owner_role if template fetch fails
        }
      }
      
      const docRef = doc(db, 'eSignDocuments', documentId);
      // Clean undefined values before updating
      const cleanedUpdates = removeUndefined({
        ...updates,
        ...(ownerRole !== undefined && { owner_role: ownerRole }),
        updatedAt: serverTimestamp()
      });
      await updateDoc(docRef, cleanedUpdates);
      return { success: true };
    } catch (error: any) {
      console.error('Error updating agreement document:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a document
  deleteDocument: async (documentId: string) => {
    try {
      const docRef = doc(db, 'eSignDocuments', documentId);
      await deleteDoc(docRef);
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting agreement document:', error);
      return { success: false, error: error.message };
    }
  },

  // Send document for signature (can be called multiple times to re-send)
  sendForSignature: async (documentId: string, signers: AgreementSigner[], expiresAt?: Date) => {
    try {
      const docRef = doc(db, 'eSignDocuments', documentId);
      const updates: any = {
        status: 'sent',
        signers: signers.map(s => ({ ...s, status: 'pending' as AgreementSigner['status'] })), // Reset all signers to pending for re-send
        updatedAt: serverTimestamp(),
        sentAt: serverTimestamp() // Track when it was sent
      };
      
      if (expiresAt) {
        updates.expiresAt = expiresAt;
      }
      
      await updateDoc(docRef, updates);
      return { success: true };
    } catch (error: any) {
      console.error('Error sending document for signature:', error);
      return { success: false, error: error.message };
    }
  },

  // Update signer status (when they sign or decline)
  updateSignerStatus: async (
    documentId: string,
    signerEmail: string,
    status: AgreementSigner['status'],
    signedAt?: Date,
    declineReason?: string,
    signatureUrl?: string
  ) => {
    try {
      const docResult = await agreementService.getDocument(documentId);
      if (!docResult.success || !docResult.data) {
        return { success: false, error: 'Document not found' };
      }

      const document = docResult.data;
      const updatedSigners = document.signers.map(signer => {
        if (signer.email === signerEmail) {
          return {
            ...signer,
            status,
            signedAt: signedAt || signer.signedAt,
            declineReason: declineReason || signer.declineReason,
            signatureUrl: signatureUrl || signer.signatureUrl
          };
        }
        return signer;
      });

      // Check if all signers have signed
      const allSigned = updatedSigners.every(s => s.status === 'signed');
      const documentStatus = allSigned ? 'signed' : document.status;

      const docRef = doc(db, 'eSignDocuments', documentId);
      await updateDoc(docRef, {
        signers: updatedSigners,
        status: documentStatus,
        completedAt: allSigned ? serverTimestamp() : document.completedAt,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error updating signer status:', error);
      return { success: false, error: error.message };
    }
  }
};

